import { useState, useEffect, useCallback } from 'react';
import {
  DatabaseBackup, Download, RotateCcw, RefreshCw, Loader2, CheckCircle, XCircle,
  Clock, Cloud, HardDrive, CalendarClock, AlertTriangle, X, ShieldCheck, Trash2, Settings, PlugZap, Save
} from 'lucide-react';
import ConfirmModal from '../../components/ConfirmModal';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { respaldosApi } from '../../utils/api';

// Respaldos de ESTA empresa: base de datos completa + archivos (PDF, XML, comprobantes),
// guardados en la nube fuera del servidor. Lo estándar lo define el proveedor del servicio (SuperAdmin);
// la empresa puede usar su propio Azure y su propia programación. Nunca se nombra al proveedor.

const TIPO = {
  manual: { label: 'Manual', color: 'bg-blue-100 text-blue-700' },
  automatico: { label: 'Automático', color: 'bg-gray-100 text-gray-700' },
  antes_de_restaurar: { label: 'Antes de restaurar', color: 'bg-amber-100 text-amber-700' }
};

const FRECUENCIA = { daily: 'todos los días', weekly: 'los domingos', monthly: 'el día 1 de cada mes' };

const formatBytes = (b) => {
  const n = Number(b) || 0;
  if (!n) return '-';
  const u = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), u.length - 1);
  return `${(n / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
};

const formatFecha = (d) => d
  ? new Date(d).toLocaleString('es-CR', { timeZone: 'America/Costa_Rica', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '-';

function EstadoBadge({ log }) {
  if (log.estado === 'en_curso') {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700"><Loader2 className="h-3 w-3 animate-spin" />En curso</span>;
  }
  if (log.estado === 'fallido') {
    return <span title={log.error_message || ''} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 cursor-help"><XCircle className="h-3 w-3" />Fallido</span>;
  }
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700"><CheckCircle className="h-3 w-3" />Exitoso</span>;
}

function Restauracion({ log }) {
  if (log.restauracion_estado === 'en_curso') {
    return <span className="inline-flex items-center gap-1 text-xs text-blue-700"><Loader2 className="h-3 w-3 animate-spin" />Restaurando…</span>;
  }
  if (log.restauracion_estado === 'fallida') {
    return <span title={log.restauracion_error || ''} className="text-xs text-red-600 cursor-help">Restauración fallida</span>;
  }
  if (log.restaurado_at) {
    return <span className="text-xs text-purple-700" title={log.restaurado_por || ''}>Restaurado {formatFecha(log.restaurado_at)}</span>;
  }
  return <span className="text-xs text-gray-400">-</span>;
}

// Dónde quedó el archivo: hoy siempre en Azure (blobbobkuantaapps / finance360 / <slug>/)
function Ubicacion({ log }) {
  if (log.estado === 'fallido' || !log.blob_name) return <span className="text-xs text-gray-400">-</span>;
  if (log.estado === 'en_curso') return <span className="text-xs text-gray-400">Subiendo…</span>;
  const propio = log.destino === 'propio';
  const carpeta = log.blob_name.includes('/') ? `${log.blob_name.slice(0, log.blob_name.lastIndexOf('/'))}/` : '';
  return (
    <span className={clsx('inline-flex items-center gap-1 text-xs', propio ? 'text-indigo-700' : 'text-blue-700')}
      title={`${log.destino_cuenta || ''}/${log.destino_contenedor || ''}/${log.blob_name}`}>
      <Cloud className="h-3.5 w-3.5" /> {propio ? 'Azure de la empresa' : 'Nube del sistema'}
      <span className="text-gray-400 font-mono">· {log.destino_contenedor || 'finance360'}/{carpeta}</span>
    </span>
  );
}

function Tarjeta({ icon: Icon, color, titulo, valor, detalle }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-start gap-3">
      <div className={clsx('p-2 rounded-lg', color)}><Icon className="h-5 w-5" /></div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500">{titulo}</p>
        <p className="text-base font-semibold text-gray-900 truncate">{valor}</p>
        {detalle && <p className="text-xs text-gray-500 truncate">{detalle}</p>}
      </div>
    </div>
  );
}

export default function Respaldos() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creando, setCreando] = useState(false);
  const [descargando, setDescargando] = useState(null);
  const [aRestaurar, setARestaurar] = useState(null);
  const [confirmacion, setConfirmacion] = useState('');
  const [restaurando, setRestaurando] = useState(false);
  const [aEliminar, setAEliminar] = useState(null);
  const [vista, setVista] = useState('empresa'); // 'empresa' (la seleccionada) | 'cuenta' (todas)
  const [confirmarTodos, setConfirmarTodos] = useState(false);
  const [borrandoTodos, setBorrandoTodos] = useState(false);
  const [cfgForm, setCfgForm] = useState(null); // formulario de Configuración (null = cerrado)
  const [probando, setProbando] = useState(false);
  const [guardandoCfg, setGuardandoCfg] = useState(false);

  const cargar = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    try {
      const res = await respaldosApi.listar();
      setData(res.data.data);
    } catch (error) {
      if (!silencioso) toast.error(error.response?.data?.error || 'Error al cargar los respaldos');
    } finally {
      if (!silencioso) setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const esCuenta = vista === 'cuenta' && data?.puedeCuenta;
  const logs = (esCuenta ? data?.logsCuenta : data?.logsEmpresa) || [];
  const todos = [...(data?.logsEmpresa || []), ...(data?.logsCuenta || [])];
  const hayActividad = data?.enCurso || todos.some(l => l.estado === 'en_curso' || l.restauracion_estado === 'en_curso');
  const nombreEmpresa = data?.empresa?.nombre || 'esta empresa';
  const nEmpresas = data?.empresasCuenta?.length || 0;

  // Mientras hay un respaldo o una restauración corriendo, refrescar cada 5 s
  useEffect(() => {
    if (!hayActividad) return;
    const t = setInterval(() => cargar(true), 5000);
    return () => clearInterval(t);
  }, [hayActividad, cargar]);

  const exitosos = logs.filter(l => l.estado === 'exitoso');
  const ultimo = exitosos[0];
  const espacio = exitosos.reduce((s, l) => s + (Number(l.size_bytes) || 0), 0);
  const prog = data?.programacion;
  const destino = data?.destino;
  const destinoListo = !!destino?.listo;
  const hhmm = (h, m) => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

  const abrirConfig = () => {
    setCfgForm({
      tipo: destino?.tipo || 'kuanta',
      azureConnectionString: '',
      azureContainer: destino?.contenedor || '',
      carpeta: destino?.carpeta || '',
      propia: !!prog?.propia,
      enabled: prog?.enabled ?? true,
      frequency: prog?.frequency || 'daily',
      scheduleHour: prog?.scheduleHour ?? 2,
      scheduleMinute: prog?.scheduleMinute ?? 0,
      retentionDays: prog?.retentionDays ?? 30
    });
  };

  const handleProbar = async () => {
    setProbando(true);
    try {
      const res = await respaldosApi.probarDestino({
        azureConnectionString: cfgForm.azureConnectionString,
        azureContainer: cfgForm.azureContainer,
        carpeta: cfgForm.carpeta
      });
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.error || 'La conexión falló');
    } finally {
      setProbando(false);
    }
  };

  const handleGuardarConfig = async () => {
    setGuardandoCfg(true);
    try {
      // Destino (el propio se prueba en el servidor antes de guardarse)
      await respaldosApi.guardarDestino({
        tipo: cfgForm.tipo,
        azureConnectionString: cfgForm.azureConnectionString || undefined,
        azureContainer: cfgForm.azureContainer,
        carpeta: cfgForm.carpeta
      });
      await respaldosApi.guardarProgramacion({
        propia: cfgForm.propia,
        enabled: cfgForm.enabled,
        frequency: cfgForm.frequency,
        scheduleHour: cfgForm.scheduleHour,
        scheduleMinute: cfgForm.scheduleMinute,
        retentionDays: cfgForm.retentionDays
      });
      toast.success('Configuración de respaldos guardada');
      setCfgForm(null);
      await cargar(true);
    } catch (error) {
      toast.error(error.response?.data?.error || 'No se pudo guardar la configuración');
    } finally {
      setGuardandoCfg(false);
    }
  };

  const handleCrear = async () => {
    setCreando(true);
    try {
      const res = await respaldosApi.crear(esCuenta ? 'cuenta' : 'empresa');
      toast.success(res.data.message || 'Respaldo iniciado');
      await cargar(true);
    } catch (error) {
      toast.error(error.response?.data?.error || 'No se pudo iniciar el respaldo');
    } finally {
      setCreando(false);
    }
  };

  const handleDescargar = async (log) => {
    setDescargando(log.id);
    try {
      const res = await respaldosApi.descargar(log.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = log.file_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      let msg = 'Error al descargar el respaldo';
      try { msg = JSON.parse(await error.response?.data?.text())?.error || msg; } catch { /* respuesta binaria */ }
      toast.error(msg);
    } finally {
      setDescargando(null);
    }
  };

  const handleRestaurar = async () => {
    if (!aRestaurar) return;
    setRestaurando(true);
    try {
      const res = await respaldosApi.restaurar(aRestaurar.id, confirmacion);
      toast.success(res.data.message || 'Restauración iniciada');
      setARestaurar(null);
      setConfirmacion('');
      await cargar(true);
    } catch (error) {
      toast.error(error.response?.data?.error || 'No se pudo iniciar la restauración');
    } finally {
      setRestaurando(false);
    }
  };

  const handleEliminarTodos = async () => {
    setConfirmarTodos(false);
    setBorrandoTodos(true);
    try {
      const res = await respaldosApi.eliminarTodos(esCuenta ? 'cuenta' : 'empresa');
      toast.success(res.data.message);
      await cargar(true);
    } catch (error) {
      toast.error(error.response?.data?.error || 'No se pudieron borrar los respaldos');
    } finally {
      setBorrandoTodos(false);
    }
  };

  const handleEliminar = async () => {
    const log = aEliminar;
    setAEliminar(null);
    try {
      await respaldosApi.eliminar(log.id);
      toast.success('Respaldo eliminado');
      await cargar(true);
    } catch (error) {
      toast.error(error.response?.data?.error || 'No se pudo eliminar el respaldo');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Alcance: esta empresa | cuenta completa (solo quien administra la cuenta) */}
      {data?.puedeCuenta && (
        <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-fit">
          {[['empresa', `Esta empresa: ${nombreEmpresa}`], ['cuenta', `Cuenta completa (${nEmpresas} ${nEmpresas === 1 ? 'empresa' : 'empresas'})`]].map(([k, label]) => (
            <button key={k} onClick={() => setVista(k)}
              className={clsx('px-4 py-1.5 rounded-md text-sm font-medium transition-colors', vista === k ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900')}>
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Acciones */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
          <p className="text-sm text-gray-600">
            {esCuenta ? (
              <>Respaldo de la <span className="font-medium text-gray-900">cuenta completa</span>: la base de datos de
                <span className="font-medium text-gray-900"> las {nEmpresas} empresas</span> y todos sus archivos. Es para recuperación ante desastres: restaurarlo afecta a todas las empresas. Se guarda</>
            ) : (
              <>Respaldo de <span className="font-medium text-gray-900">{nombreEmpresa}</span>: todos sus datos (facturas, clientes,
                contabilidad, configuración) y sus archivos (PDF, XML, comprobantes). No incluye a las demás empresas. Se guarda</>
            )}
            {destino?.tipo === 'propio'
              ? <> en <span className="font-medium text-gray-900">su propio Azure</span> ({destino.cuenta}/{destino.contenedor})</>
              : ' en la nube del sistema, fuera del servidor'}
            {prog ? `, por ${prog.retentionDays} días` : ''}.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {data?.puedeCuenta && <button
            onClick={abrirConfig}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm text-gray-700"
            title="Destino, programación y retención"
          >
            <Settings className="h-4 w-4" /> Configurar
          </button>}
          <button
            onClick={() => cargar()}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            title="Actualizar"
          >
            <RefreshCw className="h-4 w-4 text-gray-600" />
          </button>
          <button
            onClick={handleCrear}
            disabled={creando || hayActividad || !destinoListo}
            title={!destinoListo ? 'El destino de respaldos no está configurado' : ''}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-500 text-emerald-700 hover:bg-emerald-50 font-medium text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {creando || data?.enCurso ? <Loader2 className="h-4 w-4 animate-spin" /> : <DatabaseBackup className="h-4 w-4" />}
            {data?.enCurso ? 'Respaldo en curso…' : (esCuenta ? 'Respaldar la cuenta' : 'Respaldar esta empresa')}
          </button>
          <button
            onClick={() => setConfirmarTodos(true)}
            disabled={borrandoTodos || hayActividad || logs.length === 0}
            title={esCuenta ? 'Borrar todos los respaldos de la cuenta completa' : `Borrar todos los respaldos de ${nombreEmpresa}`}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-red-400 text-red-600 hover:bg-red-50 font-medium text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {borrandoTodos ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Borrar todos
          </button>
        </div>
      </div>

      {!destinoListo && (
        <div className="flex items-center gap-2 p-3 rounded-lg border border-amber-300 bg-amber-50 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          El destino de los respaldos todavía no está configurado. Contacte a soporte.
        </div>
      )}

      {/* Resumen */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Tarjeta icon={CheckCircle} color="bg-green-100 text-green-700" titulo="Último respaldo"
          valor={ultimo ? formatFecha(ultimo.completed_at || ultimo.created_at) : 'Ninguno todavía'}
          detalle={ultimo ? `${TIPO[ultimo.tipo]?.label || ultimo.tipo} · ${formatBytes(ultimo.size_bytes)}` : null} />
        <Tarjeta icon={Cloud} color="bg-blue-100 text-blue-700" titulo="Respaldos guardados"
          valor={exitosos.length} detalle={`${formatBytes(espacio)} en total`} />
        <Tarjeta icon={CalendarClock} color="bg-purple-100 text-purple-700" titulo="Próximo automático"
          valor={prog?.enabled ? formatFecha(prog.proxima) : 'Desactivado'}
          detalle={prog?.enabled ? `${FRECUENCIA[prog.frequency] || prog.frequency} a las ${hhmm(prog.scheduleHour, prog.scheduleMinute)} · ${prog.propia ? 'programación de la empresa' : 'programación estándar'}` : (prog?.propia ? 'Desactivado por la empresa' : null)} />
        <Tarjeta icon={HardDrive} color="bg-gray-100 text-gray-700" titulo="Retención"
          valor={prog ? `${prog.retentionDays} días` : '-'} detalle={`${prog?.propia ? 'Definida por la empresa' : 'Estándar del sistema'} · el último nunca se borra`} />
      </div>

      {/* Historial */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
          <Clock className="h-4 w-4 text-gray-500" />
          <h3 className="font-semibold text-gray-900 text-sm">{esCuenta ? 'Respaldos de la cuenta completa' : `Respaldos de ${nombreEmpresa}`}</h3>
        </div>
        {logs.length === 0 ? (
          <div className="p-10 text-center">
            <DatabaseBackup className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">{esCuenta ? 'Todavía no hay respaldos de la cuenta completa' : `Todavía no hay respaldos de ${nombreEmpresa}`}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-gray-600">
                  <th className="text-left px-4 py-2 font-medium">Fecha</th>
                  <th className="text-left px-4 py-2 font-medium">Tipo</th>
                  <th className="text-left px-4 py-2 font-medium">Hecho por</th>
                  <th className="text-right px-4 py-2 font-medium">Tamaño</th>
                  <th className="text-right px-4 py-2 font-medium">Archivos</th>
                  <th className="text-left px-4 py-2 font-medium">Ubicación</th>
                  <th className="text-center px-4 py-2 font-medium">Estado</th>
                  <th className="text-left px-4 py-2 font-medium">Restauración</th>
                  <th className="text-right px-4 py-2 font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 whitespace-nowrap text-gray-900">{formatFecha(log.created_at)}</td>
                    <td className="px-4 py-2">
                      <span className={clsx('px-2 py-0.5 rounded text-xs font-medium', TIPO[log.tipo]?.color)}>{TIPO[log.tipo]?.label || log.tipo}</span>
                    </td>
                    <td className="px-4 py-2 text-gray-600">{log.creado_por || (log.origen === 'programado' ? 'Programado' : '-')}</td>
                    <td className="px-4 py-2 text-right text-gray-700 whitespace-nowrap">{formatBytes(log.size_bytes)}</td>
                    <td className="px-4 py-2 text-right text-gray-700">
                      {log.archivos_count ?? '-'}
                      {log.archivos_faltantes > 0 && (
                        <span className="ml-1 text-amber-600" title={`${log.archivos_faltantes} archivo(s) referenciados en la base ya no existían al respaldar`}>
                          (+{log.archivos_faltantes} sin archivo)
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap"><Ubicacion log={log} /></td>
                    <td className="px-4 py-2 text-center"><EstadoBadge log={log} /></td>
                    <td className="px-4 py-2 whitespace-nowrap"><Restauracion log={log} /></td>
                    <td className="px-4 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleDescargar(log)}
                          disabled={log.estado !== 'exitoso' || descargando === log.id}
                          title="Descargar (.zip)"
                          className="p-1.5 rounded border border-teal-300 text-teal-600 hover:bg-teal-50 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          {descargando === log.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => { setARestaurar(log); setConfirmacion(''); }}
                          disabled={log.estado !== 'exitoso' || hayActividad}
                          title="Restaurar este respaldo"
                          className="p-1.5 rounded border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setAEliminar(log)}
                          disabled={log.estado === 'en_curso' || log.restauracion_estado === 'en_curso'}
                          title="Eliminar este respaldo"
                          className="p-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {cfgForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !guardandoCfg && setCfgForm(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Settings className="h-5 w-5" /> Configuración de respaldos</h3>
              <button onClick={() => setCfgForm(null)} disabled={guardandoCfg} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 grid md:grid-cols-2 gap-6 text-sm">
              {/* Destino */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2"><Cloud className="h-4 w-4" /> ¿Dónde se guardan?</h4>
                <label className={clsx('flex items-start gap-2 p-3 rounded-lg border cursor-pointer', cfgForm.tipo === 'kuanta' ? 'border-blue-500 bg-blue-50' : 'border-gray-200')}>
                  <input type="radio" className="mt-0.5" checked={cfgForm.tipo === 'kuanta'} onChange={() => setCfgForm({ ...cfgForm, tipo: 'kuanta' })} />
                  <span><span className="font-medium text-gray-900">Almacenamiento del sistema</span><span className="block text-xs text-gray-500">Incluido en el servicio y administrado por el proveedor. No requiere configuración.</span></span>
                </label>
                <label className={clsx('flex items-start gap-2 p-3 rounded-lg border cursor-pointer', cfgForm.tipo === 'propio' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200')}>
                  <input type="radio" className="mt-0.5" checked={cfgForm.tipo === 'propio'} onChange={() => setCfgForm({ ...cfgForm, tipo: 'propio' })} />
                  <span><span className="font-medium text-gray-900">Azure Blob Storage de la empresa</span><span className="block text-xs text-gray-500">Los respaldos se guardan en una cuenta de almacenamiento propiedad de la empresa.</span></span>
                </label>
                {cfgForm.tipo === 'propio' && (
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-gray-700">
                      Cadena de conexión o SAS del contenedor
                      {destino?.tipo === 'propio' && <span className="text-green-700 font-normal"> · guardada ({destino.cuenta}); vacía = no cambiarla</span>}
                      <input type="password" autoComplete="off" value={cfgForm.azureConnectionString}
                        onChange={e => setCfgForm({ ...cfgForm, azureConnectionString: e.target.value })}
                        placeholder="BlobEndpoint=https://<cuenta>.blob.core.windows.net/;SharedAccessSignature=..."
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-xs" />
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="block text-xs font-medium text-gray-700">
                        Contenedor
                        <input value={cfgForm.azureContainer} onChange={e => setCfgForm({ ...cfgForm, azureContainer: e.target.value.toLowerCase() })}
                          placeholder="respaldos" className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
                      </label>
                      <label className="block text-xs font-medium text-gray-700">
                        Carpeta (opcional)
                        <input value={cfgForm.carpeta} onChange={e => setCfgForm({ ...cfgForm, carpeta: e.target.value })}
                          placeholder="finance360" className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
                      </label>
                    </div>
                    <p className="text-xs text-gray-500">Permisos necesarios: leer, escribir, crear, borrar y listar (racwdl). El contenedor debe existir.</p>
                    <button onClick={handleProbar} disabled={probando || !cfgForm.azureConnectionString || !cfgForm.azureContainer}
                      title={!cfgForm.azureConnectionString ? 'Pegue la cadena para probarla (al guardar se prueba la guardada)' : ''}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-indigo-400 text-indigo-700 hover:bg-indigo-50 text-xs disabled:opacity-40">
                      {probando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlugZap className="h-3.5 w-3.5" />} Probar conexión
                    </button>
                  </div>
                )}
                <p className="text-xs text-gray-500">Los respaldos que ya existen se quedan donde están y se pueden seguir bajando y restaurando.</p>
              </div>

              {/* Programación y retención */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2"><CalendarClock className="h-4 w-4" /> Respaldos automáticos y retención</h4>
                <label className={clsx('flex items-start gap-2 p-3 rounded-lg border cursor-pointer', !cfgForm.propia ? 'border-blue-500 bg-blue-50' : 'border-gray-200')}>
                  <input type="radio" className="mt-0.5" checked={!cfgForm.propia} onChange={() => setCfgForm({ ...cfgForm, propia: false })} />
                  <span>
                    <span className="font-medium text-gray-900">Configuración estándar del sistema</span>
                    <span className="block text-xs text-gray-500">
                      {prog?.global?.enabled
                        ? `Definida por el proveedor del servicio: ${FRECUENCIA[prog.global.frequency]} a las ${hhmm(prog.global.scheduleHour, prog.global.scheduleMinute)}, se conservan ${prog.global.retentionDays} días`
                        : 'Definida por el proveedor del servicio: automático desactivado'}
                    </span>
                  </span>
                </label>
                <label className={clsx('flex items-start gap-2 p-3 rounded-lg border cursor-pointer', cfgForm.propia ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200')}>
                  <input type="radio" className="mt-0.5" checked={cfgForm.propia} onChange={() => setCfgForm({ ...cfgForm, propia: true })} />
                  <span><span className="font-medium text-gray-900">Configuración personalizada</span><span className="block text-xs text-gray-500">Frecuencia, hora y retención definidas por la empresa.</span></span>
                </label>
                {cfgForm.propia && (
                  <div className="grid grid-cols-2 gap-3">
                    <label className="col-span-2 flex items-center gap-2 text-gray-700">
                      <input type="checkbox" checked={cfgForm.enabled} onChange={e => setCfgForm({ ...cfgForm, enabled: e.target.checked })} />
                      Respaldar automáticamente
                    </label>
                    <label className="block text-xs font-medium text-gray-700">
                      Frecuencia
                      <select value={cfgForm.frequency} disabled={!cfgForm.enabled} onChange={e => setCfgForm({ ...cfgForm, frequency: e.target.value })}
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-50">
                        <option value="daily">Todos los días</option>
                        <option value="weekly">Semanal (domingos)</option>
                        <option value="monthly">Mensual (día 1)</option>
                      </select>
                    </label>
                    <label className="block text-xs font-medium text-gray-700">
                      Hora (Costa Rica)
                      <input type="time" disabled={!cfgForm.enabled}
                        value={hhmm(cfgForm.scheduleHour, cfgForm.scheduleMinute)}
                        onChange={e => { const [h, m] = e.target.value.split(':'); setCfgForm({ ...cfgForm, scheduleHour: parseInt(h, 10) || 0, scheduleMinute: parseInt(m, 10) || 0 }); }}
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-50" />
                    </label>
                    <label className="block text-xs font-medium text-gray-700 col-span-2">
                      Guardar los respaldos por (días)
                      <input type="number" min="1" max="3650" value={cfgForm.retentionDays}
                        onChange={e => setCfgForm({ ...cfgForm, retentionDays: e.target.value })}
                        className="mt-1 w-40 px-3 py-2 border border-gray-300 rounded-lg" />
                      <span className="block text-gray-500 font-normal mt-1">Los más viejos se borran solos. El último respaldo nunca se borra.</span>
                    </label>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setCfgForm(null)} disabled={guardandoCfg} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 text-sm">Cancelar</button>
              <button onClick={handleGuardarConfig} disabled={guardandoCfg || (cfgForm.tipo === 'propio' && (!cfgForm.azureContainer || (!cfgForm.azureConnectionString && destino?.tipo !== 'propio')))}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-500 text-emerald-700 hover:bg-emerald-50 text-sm font-medium disabled:opacity-40">
                {guardandoCfg ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {guardandoCfg && cfgForm.tipo === 'propio' ? 'Probando y guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmarTodos}
        onClose={() => setConfirmarTodos(false)}
        onConfirm={handleEliminarTodos}
        variant="danger"
        title={esCuenta ? 'Borrar todos los respaldos de la cuenta' : `Borrar todos los respaldos de ${nombreEmpresa}`}
        message={`Se borrarán de la nube los ${logs.length} respaldo(s) de ${esCuenta ? 'la cuenta completa' : nombreEmpresa} (${formatBytes(logs.reduce((s, l) => s + (Number(l.size_bytes) || 0), 0))}). ${esCuenta ? 'Los respaldos por empresa no se tocan. ' : ''}La empresa quedará sin respaldo hasta el próximo. No se puede deshacer.`}
        confirmText="Borrar todos"
      />

      <ConfirmModal
        isOpen={!!aEliminar}
        onClose={() => setAEliminar(null)}
        onConfirm={handleEliminar}
        variant="danger"
        title="Eliminar respaldo"
        message={aEliminar ? `Se borra de Azure el respaldo del ${formatFecha(aEliminar.created_at)} (${formatBytes(aEliminar.size_bytes)}). ${exitosos.length === 1 && aEliminar.estado === 'exitoso' ? (esCuenta ? 'Es el ÚNICO respaldo de la cuenta completa. ' : `Es el ÚNICO respaldo de ${nombreEmpresa}: quedará sin respaldo hasta el próximo. `) : ''}No se puede deshacer.` : ''}
        confirmText="Eliminar"
      />

      {/* Confirmar restauración */}
      {aRestaurar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !restaurando && setARestaurar(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b bg-red-50 rounded-t-xl">
              <h3 className="text-lg font-semibold text-red-800 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" /> {aRestaurar.alcance === 'cuenta' ? 'Restaurar la cuenta completa' : `Restaurar ${aRestaurar.emisor_nombre || nombreEmpresa}`}
              </h3>
              <button onClick={() => setARestaurar(null)} disabled={restaurando} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 grid md:grid-cols-2 gap-5 text-sm">
              <div className="space-y-2">
                {aRestaurar.alcance === 'cuenta' ? (
                  <p className="text-gray-700">
                    <span className="font-semibold text-red-700">Las {nEmpresas} empresas de la cuenta</span> volverán a como estaban el
                    <span className="font-semibold"> {formatFecha(aRestaurar.created_at)}</span>, incluidos usuarios y configuración.
                  </p>
                ) : (
                  <p className="text-gray-700">
                    Los datos de <span className="font-semibold">{aRestaurar.emisor_nombre || nombreEmpresa}</span> volverán a como estaban el
                    <span className="font-semibold"> {formatFecha(aRestaurar.created_at)}</span>. <span className="font-semibold">Las demás empresas no se tocan.</span>
                  </p>
                )}
                <p className="text-gray-700">Lo hecho después de esa fecha <span className="font-semibold text-red-700">se pierde</span>, incluidos comprobantes emitidos (seguirán existiendo en Hacienda).</p>
                <ul className="text-gray-500 text-xs list-disc pl-4 space-y-0.5">
                  <li>Antes se toma un respaldo del estado actual, por si hay que volver atrás.</li>
                  <li>Los consecutivos de Hacienda nunca retroceden.</li>
                  {aRestaurar.alcance !== 'cuenta' && <li>Se conservan los accesos de usuarios, certificados y credenciales vigentes.</li>}
                </ul>
              </div>
              <div className="space-y-3">
                <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-xs text-gray-600 space-y-1">
                  <p><span className="text-gray-500">Respaldo:</span> {TIPO[aRestaurar.tipo]?.label} · {formatBytes(aRestaurar.size_bytes)}</p>
                  <p><span className="text-gray-500">Archivos:</span> {aRestaurar.archivos_count ?? 0}</p>
                </div>
                <label className="block text-sm font-medium text-gray-700">
                  Escriba <span className="font-mono font-bold text-red-700">RESTAURAR</span> para confirmar
                  <input
                    autoFocus
                    value={confirmacion}
                    onChange={e => setConfirmacion(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg font-mono focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setARestaurar(null)} disabled={restaurando} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 text-sm">Cancelar</button>
              <button
                onClick={handleRestaurar}
                disabled={confirmacion.trim().toUpperCase() !== 'RESTAURAR' || restaurando}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-red-500 text-red-700 hover:bg-red-50 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {restaurando ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                Restaurar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
